import { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const STAGES = ['UPLOADED', 'CONVERTING', 'VALIDATING', 'REPORTING', 'COMPLETE'];

const STATUS_COLOR = {
  UPLOADED:   '#888',
  CONVERTING: '#f90',
  VALIDATING: '#0073bb',
  REPORTING:  '#9b59b6',
  COMPLETE:   '#27ae60',
  FAILED:     '#c0392b',
};

export default function JobDashboard({ highlightJobId, onViewResults }) {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const res  = await fetch(`${API_BASE}/api/jobs`);
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : data.jobs || []);
    } catch (e) {
      console.error('Failed to load jobs:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    pollRef.current = setInterval(fetchJobs, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2>Validation Jobs</h2>
        <small style={{ color: '#888' }}>Auto-refreshes every 10 s</small>
      </div>

      {loading && <p style={{ color: '#888', marginTop: 24 }}>Loading jobs…</p>}

      {!loading && jobs.length === 0 && (
        <div style={s.emptyBox}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <h3 style={{ margin: '0 0 8px', color: '#333' }}>No jobs yet</h3>
          <p style={{ color: '#666', fontSize: 14 }}>
            Submit a validation job from the <strong>Upload</strong> tab to see it here.
          </p>
        </div>
      )}

      {jobs.length > 0 && (
        <table style={s.table}>
          <thead>
            <tr>
              {['Job ID', 'Files', 'Submitted', 'Status', 'Progress', 'Action'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map(job => (
              <tr key={job.jobId}
                style={{ background: job.jobId === highlightJobId ? '#fffbe6' : 'transparent' }}>
                <td style={{ ...s.td, maxWidth: 320 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{job.jobId}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(job.jobId)}
                      style={{ padding: '2px 6px', fontSize: 10, background: '#f0f4f8', border: '1px solid #ccc', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      title="Copy Job ID">
                      📋
                    </button>
                  </div>
                </td>
                <td style={s.td}>{job.fileCount} file(s)</td>
                <td style={s.td}>{new Date(Number(job.createdAt)).toLocaleString()}</td>
                <td style={s.td}><StatusBadge status={job.status} /></td>
                <td style={s.td}><ProgressBar status={job.status} /></td>
                <td style={s.td}>
                  {job.status === 'COMPLETE' && (
                    <button style={s.viewBtn} onClick={() => onViewResults(job.jobId)}>
                      View Results
                    </button>
                  )}
                  {job.status === 'FAILED' && (
                    <span style={{ color: '#c00', fontSize: 12 }}>{job.errorMessage || 'Failed'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span style={{
      background: STATUS_COLOR[status] || '#888',
      color: '#fff', borderRadius: 12,
      padding: '2px 10px', fontSize: 12, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

function ProgressBar({ status }) {
  const idx   = STAGES.indexOf(status);
  const pct   = status === 'FAILED' ? 100 : Math.round(((idx + 1) / STAGES.length) * 100);
  const color = STATUS_COLOR[status] || '#888';
  return (
    <div style={{ background: '#eee', borderRadius: 4, height: 8, width: 120 }}>
      <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.4s' }} />
    </div>
  );
}

const s = {
  page:     { maxWidth: 900, margin: '0 auto', padding: 24, fontFamily: 'sans-serif' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table:    { width: '100%', borderCollapse: 'collapse', marginTop: 16 },
  th:       { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #ddd', background: '#f5f5f5' },
  td:       { padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
  viewBtn:  { padding: '4px 12px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  center:   { textAlign: 'center', marginTop: 40 },
  emptyBox: { textAlign: 'center', padding: '60px 24px', background: '#fff', borderRadius: 8, border: '1px solid #eee', marginTop: 24 },
};
